/**
 * Shipping Method Priority Filter
 *
 * Implements 7-level cascading priority filtering for shipping methods
 * based on keywords found in method names. When multiple product types
 * are in the cart (e.g., sofa + lamps), only the shipping methods for
 * the highest-priority product type are displayed.
 *
 * Priority 1 (Highest): "lift", "flat" (4-man lift items)
 * Priority 2: "standard", "deluxe", "pick", "classic" (made-to-order)
 * Priority 3: "metro" (compression items)
 * Priority 4: "lamps"
 * Priority 5: "cushion"
 * Priority 6: "1977 covers"
 * Priority 7 (Lowest): "post", "postal" (small postage / care kits)
 *
 * Non-priority methods (no matching keywords) always display.
 *
 * @see Confluence: Checkout - update shipping methods display rules
 */

const PRIORITY_LEVELS = [
  {level: 1, keywords: ['lift', 'flat']},
  {level: 2, keywords: ['standard', 'deluxe', 'pick', 'classic']},
  {level: 3, keywords: ['metro']},
  {level: 4, keywords: ['lamps']},
  {level: 5, keywords: ['cushion']},
  {level: 6, keywords: ['1977 covers']},
  {level: 7, keywords: ['post', 'postal']}
];

/**
 * Determine the priority level of a shipping method by its name.
 *
 * @param {string} methodName - Shipping method name
 * @returns {number|null} Priority level (1-7) or null for non-priority methods
 */
export function getMethodPriority(methodName) {
  if (!methodName) {
    return null;
  }

  const nameLower = methodName.toLowerCase();

  for (const {level, keywords} of PRIORITY_LEVELS) {
    if (keywords.some((keyword) => nameLower.includes(keyword))) {
      return level;
    }
  }

  return null;
}

/**
 * Apply cascading priority filter to shipping methods.
 *
 * Finds the highest available priority level (lowest number) among the
 * methods, then hides all methods at lower priority levels. Methods
 * without any priority keywords are always displayed.
 *
 * @param {Array} methods - Array of shipping methods with a `name` property
 * @returns {Array} Filtered shipping methods
 *
 * @example
 * // Cart has Jasper sofa + lamps.
 * // Available methods: Deluxe (P2), Classic (P2), Customer Pick-up (P2),
 * //                    Eye Table Lamp Postal (P7), Pallino Table Lamp Postal (P7)
 * // Result: Deluxe, Classic, Customer Pick-up (P7 methods hidden by P2)
 */
export function filterByShippingPriority(methods) {
  if (!methods || methods.length === 0) {
    return methods || [];
  }

  let highestPriority = Infinity;

  for (const method of methods) {
    const priority = getMethodPriority(method.name);

    if (priority !== null && priority < highestPriority) {
      highestPriority = priority;
    }
  }

  if (highestPriority === Infinity) {
    return methods;
  }

  return methods.filter((method) => {
    const priority = getMethodPriority(method.name);

    if (priority === null) {
      return true;
    }

    return priority <= highestPriority;
  });
}

export default filterByShippingPriority;
